import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Apple, Utensils, Calendar, Receipt, Pill, LogOut, Flame, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES } from "../lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const TABS = [
  { to: "/", label: "Dashboard", short: "Home", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/foods", label: "Foods", short: "Foods", icon: Apple, id: "foods" },
  { to: "/meals", label: "Meals", short: "Meals", icon: Utensils, id: "meals" },
  { to: "/today", label: "Today", short: "Today", icon: Calendar, id: "today" },
  { to: "/supplements", label: "Supplements", short: "Supps", icon: Pill, id: "supplements" },
  { to: "/purchases", label: "Purchases", short: "Buys", icon: Receipt, id: "purchases" },
];

function CurrencyPicker() {
  const { user, setCurrency } = useAuth();
  return (
    <Select value={user?.currency || "USD"} onValueChange={setCurrency}>
      <SelectTrigger data-testid="currency-selector" className="h-9 w-[92px] rounded-full border-slate-200 bg-white font-mono text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(CURRENCIES).map(([code, sym]) => (
          <SelectItem key={code} value={code} data-testid={`currency-opt-${code}`}>
            {sym} {code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop / top header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-white shadow-sm shadow-brand/30">
              <Flame className="h-5 w-5" />
            </div>
            <span className="font-display text-lg font-extrabold tracking-tight">Meal Cost</span>
          </div>

          <nav className="ml-6 hidden items-center gap-1 md:flex">
            {TABS.map((t) => (
              <NavLink key={t.id} to={t.to} end={t.end} data-testid={`nav-tab-${t.id}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "bg-brand-light text-brand" : "text-slate-500 hover:text-slate-900"
                  }`}>
                <t.icon className="h-4 w-4" />{t.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <CurrencyPicker />
            <span className="hidden text-sm font-medium text-slate-500 sm:inline">{user?.name}</span>
            <NavLink to="/settings" data-testid="nav-settings"
              className={({ isActive }) =>
                `flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                  isActive ? "bg-brand-light text-brand" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}>
              <SettingsIcon className="h-4 w-4" />
            </NavLink>
            <button data-testid="logout-button" onClick={handleLogout}
              className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 md:pb-12">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white/90 backdrop-blur-md md:hidden">
        <div className="flex items-stretch justify-around">
          {TABS.map((t) => (
            <NavLink key={t.id} to={t.to} end={t.end} data-testid={`mobile-nav-${t.id}`}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${
                  isActive ? "text-brand" : "text-slate-400"
                }`}>
              <t.icon className="h-5 w-5" />{t.short}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
